import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import Hls from 'hls.js';
import {
  devPlayerLog,
  messageFromFetchFailure,
  messageFromMediaErrorCode,
  PLAYBACK_GENERIC_FAILURE,
} from '../utils/playbackErrors';

/** Mutable counters for one Hls instance / one attach lifecycle. */
export type HlsRecoveryCounters = { networkRetries: number; didMediaRecover: boolean };

export type HlsErrorLike = {
  fatal?: boolean;
  type?: string;
  details?: string;
  reason?: string;
  error?: Error;
};

/**
 * Attempt built-in Hls recovery for fatal network/media errors.
 * @returns true if a recovery path was invoked (caller should not set UI error yet).
 */
export function tryHandleHlsFatal(
  hls: Hls,
  data: HlsErrorLike,
  counters: HlsRecoveryCounters,
  log: typeof devPlayerLog = devPlayerLog,
): boolean {
  if (!data.fatal) {
    log('HLS non-fatal', data.type, data.details, data.reason ?? data.error);
    return true;
  }

  log('HLS fatal', data.type, data.details, data.reason ?? data.error);

  if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
    const d = data.details ?? '';
    if (
      d === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
      d === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
      d === Hls.ErrorDetails.MANIFEST_PARSING_ERROR
    ) {
      return false;
    }
    if (counters.networkRetries < 1) {
      counters.networkRetries += 1;
      log('HLS recover: startLoad()');
      try {
        hls.startLoad();
      } catch (e) {
        log('HLS startLoad failed', e);
      }
      return true;
    }
    return false;
  }

  if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
    if (!counters.didMediaRecover) {
      counters.didMediaRecover = true;
      log('HLS recover: recoverMediaError()');
      try {
        hls.recoverMediaError();
      } catch (e) {
        log('HLS recoverMediaError failed', e);
      }
      return true;
    }
    return false;
  }

  return false;
}

/** User-facing line after fatal Hls error when recovery is not possible or not attempted. */
export function userMessageForHlsFatal(data: HlsErrorLike): string {
  const d = data.details ?? '';
  if (
    d === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
    d === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT ||
    d === Hls.ErrorDetails.MANIFEST_PARSING_ERROR
  ) {
    return 'Stream unavailable.';
  }
  if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
    return 'Network issue or blocked stream.';
  }
  if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
    return 'Stream format not supported.';
  }
  return PLAYBACK_GENERIC_FAILURE;
}

export interface UsePlayerErrorHandlerResult {
  playbackError: string | null;
  clearPlaybackError: () => void;
  /** Reset Hls recovery counters when attaching a new URL. */
  resetPlaybackRecovery: () => void;
  /** Map `fetch` / adapter throws to UI copy. */
  reportFetchFailure: (e: unknown) => void;
  /** Map `<video>` MediaError code to UI copy. */
  reportVideoMediaError: (code: number | undefined | null) => void;
  /** Set arbitrary user-facing playback error. */
  reportPlaybackMessage: (message: string) => void;
  devLog: typeof devPlayerLog;
  /** Same ref for the current attach lifecycle (Hls recovery limits). */
  hlsRecoveryRef: MutableRefObject<HlsRecoveryCounters>;
}

/**
 * Centralized playback error state + helpers for `<video>` / HLS.js (no backend).
 */
export function usePlayerErrorHandler(): UsePlayerErrorHandlerResult {
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const hlsRecoveryRef = useRef<HlsRecoveryCounters>({ networkRetries: 0, didMediaRecover: false });

  const clearPlaybackError = useCallback(() => setPlaybackError(null), []);

  const resetPlaybackRecovery = useCallback(() => {
    hlsRecoveryRef.current = { networkRetries: 0, didMediaRecover: false };
  }, []);

  const reportFetchFailure = useCallback((e: unknown) => {
    devPlayerLog('fetch failure', e);
    setPlaybackError(messageFromFetchFailure(e));
  }, []);

  const reportVideoMediaError = useCallback((code: number | undefined | null) => {
    devPlayerLog('video MediaError code', code);
    setPlaybackError(messageFromMediaErrorCode(code));
  }, []);

  const reportPlaybackMessage = useCallback((message: string) => {
    devPlayerLog('playback message', message);
    setPlaybackError(message);
  }, []);

  return {
    playbackError,
    clearPlaybackError,
    resetPlaybackRecovery,
    reportFetchFailure,
    reportVideoMediaError,
    reportPlaybackMessage,
    devLog: devPlayerLog,
    hlsRecoveryRef,
  };
}
